import {
  Box,
  Button,
  Disclaimer,
  Div,
  FinancesDeliveryLossEntity,
  formatAmount,
  formatDateTimeShort,
  getFinancesDeliveryLosses,
  getTimeZone,
  H5,
  InfoComponentMobile,
  lightTheme,
  ListSpinner,
  ModalPortal,
  NumberType,
  P,
  reportError,
  TableHeader,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

const PAGE_SIZE = 20;

// Desktop table column widths — Customer gets more room, fee columns are tighter.
const COL = {
  order: '9%',
  customer: '12%',
  brand: '14%',
  location: '15%',
  date: '13%',
  dspFee: '11%',
  realDspFee: '11%',
  loss: '11%',
} as const;

const COLUMNS: { label: string; width: string }[] = [
  { label: 'Delivery', width: COL.order },
  { label: 'Customer', width: COL.customer },
  { label: 'Brand', width: COL.brand },
  { label: 'Location', width: COL.location },
  { label: 'Date Created', width: COL.date },
  { label: 'Customer DSP Fee', width: COL.dspFee },
  { label: 'Fleet DSP Fee', width: COL.realDspFee },
  { label: 'Loss', width: COL.loss },
];

const moneyOrDash = (cents: number | null) =>
  cents === null ? '—' : formatAmount(cents, NumberType.USD);

const LossAmount = ({ lossCents }: { lossCents: number }) => (
  <P $m="0" $color={lightTheme.primary} $bold>
    {formatAmount(-lossCents, NumberType.USD)}
  </P>
);

const DeliveryLossRow = ({
  delivery,
}: {
  delivery: FinancesDeliveryLossEntity;
}) => {
  const orderVDriveIdLabel = delivery.orderVDriveId.toString();
  const customerLabel = delivery.customerName || '—';
  // Open details in a new tab.
  const openNewTab = (path: string) =>
    window.open(path, '_blank', 'noopener,noreferrer');
  const goToDelivery = () =>
    openNewTab(`/deliveries/${delivery.orderVDriveId}`);
  const goToBrand = () => openNewTab(`/brand/${delivery.brandId}`);
  const goToLocation = () => openNewTab(`/locations/${delivery.locationId}`);

  return (
    <Div>
      {/* Desktop: table row — gap/padding match TableHeader for alignment */}
      <Div
        $desktop
        $flex
        $alignCenter
        $gap="12px"
        $p="12px 16px"
        $borderBottom
        $borderColor={lightTheme.lightGray}
      >
        <Div $width={COL.order}>
          <P $m="0" $pointer $hoverUnderline onClick={goToDelivery}>
            {orderVDriveIdLabel}
          </P>
        </Div>
        <Div $width={COL.customer}>
          <P $m="0">{customerLabel}</P>
        </Div>
        <Div $width={COL.brand}>
          <P $m="0" $pointer $hoverUnderline onClick={goToBrand}>
            {delivery.brandName}
          </P>
        </Div>
        <Div $width={COL.location}>
          <P $m="0" $pointer $hoverUnderline onClick={goToLocation}>
            {delivery.locationName}
          </P>
        </Div>
        <Div $width={COL.date}>
          <P $m="0">{formatDateTimeShort(delivery.createdAt)}</P>
        </Div>
        <Div $width={COL.dspFee}>
          <P $m="0">{moneyOrDash(delivery.dspFee)}</P>
        </Div>
        <Div $width={COL.realDspFee}>
          <P $m="0">{moneyOrDash(delivery.realDspFee)}</P>
        </Div>
        <Div $width={COL.loss}>
          <LossAmount lossCents={delivery.loss} />
        </Div>
      </Div>

      {/* Mobile: stacked card */}
      <Box
        $mobile
        $flex
        $column
        $gap="4px"
        $mb="8"
        $p="12px 16px"
        $pSM="12px 16px"
        style={{ boxShadow: 'none' }}
      >
        <Div $flex $alignStart $between $gap="8px">
          <Div $minWidth="0">
            <H5 $m="0">{customerLabel}</H5>
          </Div>
          <LossAmount lossCents={delivery.loss} />
        </Div>
        <InfoComponentMobile
          title="Delivery"
          component={
            <Disclaimer $m="0" $pointer $hoverUnderline onClick={goToDelivery}>
              #{orderVDriveIdLabel}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Brand"
          component={
            <Disclaimer $m="0" $pointer $hoverUnderline onClick={goToBrand}>
              {delivery.brandName}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Location"
          component={
            <Disclaimer $m="0" $pointer $hoverUnderline onClick={goToLocation}>
              {delivery.locationName}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Date Created"
          component={
            <Disclaimer $m="0">
              {formatDateTimeShort(delivery.createdAt)}
            </Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Customer DSP Fee"
          component={
            <Disclaimer $m="0">{moneyOrDash(delivery.dspFee)}</Disclaimer>
          }
        />
        <InfoComponentMobile
          title="Fleet DSP Fee"
          component={
            <Disclaimer $m="0">{moneyOrDash(delivery.realDspFee)}</Disclaimer>
          }
        />
      </Box>
    </Div>
  );
};

/**
 * "See Deliveries" trigger for the Delivery Loss chart — paginates
 * `/v1/finances/delivery-losses` in a desktop table / mobile card modal.
 */
export const SeeDeliveryLossesButton = ({
  from,
  to,
}: {
  from: string;
  to: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<FinancesDeliveryLossEntity[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sentinelRef, sentinelInView] = useInView();

  // Reset when the date range changes — the cached list belongs to the prior range.
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
  }, [from, to]);

  const fetchPage = async (nextPage: number) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await getFinancesDeliveryLosses({
        page: nextPage,
        limit: PAGE_SIZE,
        from: DateTime.fromISO(from).startOf('day').toISODate() || '',
        to: DateTime.fromISO(to).endOf('day').toISODate() || '',
        timeZone: getTimeZone,
      });
      const next = res.data.data ?? [];
      setItems((prev) => (nextPage === 1 ? next : [...prev, ...next]));
      setHasMore(next.length === PAGE_SIZE);
      setPage(nextPage);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // First fetch when the modal opens.
  useEffect(() => {
    if (isOpen && items.length === 0 && hasMore && !loading) {
      fetchPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Continue fetching while the sentinel is in view.
  useEffect(() => {
    if (sentinelInView && isOpen && hasMore && !loading && items.length > 0) {
      fetchPage(page + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelInView]);

  return (
    <>
      <Div $flexNone $mt="10px">
        <Button $wSM="110px" $w="130px" $small onClick={() => setIsOpen(true)}>
          See Deliveries
        </Button>
      </Div>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="1000px"
        maxHeight="600px"
        modalOverflow="auto"
        titleWithExit="Delivery Loss"
      >
        <TableHeader columns={COLUMNS} padding="0 16px" gap="12px" />

        {items.map((d) => (
          <DeliveryLossRow key={d.orderVDriveId} delivery={d} />
        ))}

        {items.length === 0 && !loading && (
          <Div $flex $center $height="100px">
            <P $m="0">No deliveries at loss in this period.</P>
          </Div>
        )}

        <ListSpinner loading={loading} observerRef={sentinelRef} />
      </ModalPortal>
    </>
  );
};
